import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type Documentation_CreateSpaceInput,
  type Documentation_UpdateSpaceInput,
  gqlClient,
  graphql,
  queryClient,
  type UseQueryReturnType,
} from '@tyro/api';
import { toast } from '@tyro/core/toaster';
import { useTranslation } from '@tyro/i18n';
import { documentationKeys } from './keys';

const documentationSpaces = graphql(/* GraphQL */ `
  query documentation_user_spaces {
    documentation_user_spaces {
      id
      name
      slug
      icon
      description
      sortOrder
      articleCount
      videoCount
    }
  }
`);

const createSpace = graphql(/* GraphQL */ `
  mutation documentation_admin_createSpace($input: Documentation_CreateSpaceInput!) {
    documentation_admin_createSpace(input: $input) {
      id
      name
      slug
    }
  }
`);

const updateSpace = graphql(/* GraphQL */ `
  mutation documentation_admin_updateSpace($input: Documentation_UpdateSpaceInput!) {
    documentation_admin_updateSpace(input: $input) {
      id
      name
      slug
    }
  }
`);

const deleteSpace = graphql(/* GraphQL */ `
  mutation documentation_admin_deleteSpace($id: Int!) {
    documentation_admin_deleteSpace(id: $id) {
      success
    }
  }
`);

const reorderSpaces = graphql(/* GraphQL */ `
  mutation documentation_admin_reorderSpaces($input: Documentation_ReorderSpacesInput!) {
    documentation_admin_reorderSpaces(input: $input) {
      success
    }
  }
`);

const spacesQuery = () => ({
  queryKey: documentationKeys.spaces(),
  queryFn: async () => {
    const result = await gqlClient.request(documentationSpaces);
    return result.documentation_user_spaces;
  },
});

export function getDocumentationSpaces() {
  return queryClient.fetchQuery(spacesQuery());
}

export function useDocumentationSpaces() {
  return useQuery({
    ...spacesQuery(),
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useCreateSpace() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: Documentation_CreateSpaceInput) =>
      gqlClient.request(createSpace, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.spaces(),
      });
      toast.success(t('common:snackbarMessages.createSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useUpdateSpace() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: Documentation_UpdateSpaceInput) =>
      gqlClient.request(updateSpace, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.spaces(),
      });
      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useDeleteSpace() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (id: number) => gqlClient.request(deleteSpace, { id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.spaces(),
      });
      toast.success(t('common:snackbarMessages.deleteSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useReorderSpaces() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: { spaceIds: number[] }) =>
      gqlClient.request(reorderSpaces, { input }),
    onMutate: async ({ spaceIds }) => {
      await queryClient.cancelQueries({
        queryKey: documentationKeys.spaces(),
      });
      const previous = queryClient.getQueryData<
        ReturnTypeFromUseDocumentationSpaces[]
      >(documentationKeys.spaces());
      if (previous) {
        const byId = new Map(previous.map((space) => [space.id, space]));
        const reordered = spaceIds
          .map((id) => byId.get(id))
          .filter(
            (space): space is ReturnTypeFromUseDocumentationSpaces =>
              space != null,
          );
        queryClient.setQueryData(documentationKeys.spaces(), reordered);
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(documentationKeys.spaces(), context.previous);
      }
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: documentationKeys.spaces(),
      }),
  });
}

export type ReturnTypeFromUseDocumentationSpaces = UseQueryReturnType<
  typeof useDocumentationSpaces
>[number];
